import { ImageIcon, Lock, Sparkles, Upload } from "lucide-react";

import { Badge } from "@/shared/ui/badge";
import { Button } from "@/shared/ui/button";
import { Input } from "@/shared/ui/input";
import { Label } from "@/shared/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/shared/ui/select";
import { Textarea } from "@/shared/ui/textarea";
import { cn } from "@/shared/lib/utils";

import type { FieldSpec } from "../../../api/types";
import { displayValue } from "../../../model/collect";
import { bindingSourceText, isFactField, isPlaceholder } from "../../../model/content";
import { optionLabel } from "../../../model/schemaLabels";
import { RewriteButton } from "./RewriteButton";

/**
 * 一格字段:标签行(占位「待补」/ 事实字段锁 / 来源 chip / AI 重写)+ 按类型的控件。
 * ★ 事实字段(`fact: true`)只读 + 锁 + 来源,没有「AI 重写」(ai 侧硬闸、api 403,前端干脆不给按钮);
 *   电话 / 地址天然是事实字段,模板漏标也上锁(与屏 2 清单同一条规矩,见 `isFactField`)。
 * ★ 组名走 `aria-labelledby` 指向标签行;图片字段没有可聚焦的控件(缩略图 + 两枚禁用按钮),
 *   标签画成普通文字而不是 `<label htmlFor>` —— 指向一个不存在的控件 id 等于没标。
 * ★ AI 重写送的是**框里此刻的值**(附录 A.36 `current`),不是库里的草稿。
 * ★ 图片字段空值显示「未设置(可选)」+ 一行浅提示(附录 A.41),不显示会被读成「已经有图了」的
 *   「模板默认图」;两枚按钮照旧禁用(AI 配图 / 上传都等资产库)。
 */
export function FieldEditor({
  id,
  field,
  path,
  value,
  binding,
  onChange,
  onRewrite,
}: {
  id: string;
  field: FieldSpec;
  /** 完整绑定路径(给 rewrite 与 bindings 用)。 */
  path: string;
  value: unknown;
  binding: string | undefined;
  onChange: (value: unknown) => void;
  /** 回 true = 已把新值写进去(弹层关掉)。`current` = 框里此刻的值。 */
  onRewrite: (instruction: string, current: string) => Promise<boolean>;
}) {
  const fact = isFactField(field);
  const labelId = `${id}-label`;
  const labelClass = "text-xs text-foreground";
  const text = typeof value === "string" ? value : value == null ? "" : displayValue(value);
  const source = bindingSourceText(binding);
  const rewritable = !fact && (field.type === "text" || field.type === "textarea");

  return (
    <div role="group" aria-labelledby={labelId} className="space-y-1" data-path={path}>
      <div className="flex flex-wrap items-center justify-between gap-x-2 gap-y-1">
        {field.type === "image" ? (
          <span id={labelId} className={cn("text-sm font-medium leading-none", labelClass)}>
            {field.label}
          </span>
        ) : (
          <Label id={labelId} htmlFor={id} className={labelClass}>
            {field.label}
          </Label>
        )}
        <div className="flex flex-wrap items-center gap-1.5">
          {isPlaceholder(value) && (
            <Badge
              variant="outline"
              className="border-warning/60 normal-case tracking-normal text-warning"
            >
              待补
            </Badge>
          )}
          {fact && (
            <span className="inline-flex items-center gap-0.5 text-2xs text-muted-foreground">
              <Lock className="h-3 w-3" aria-hidden />
              事实字段
            </span>
          )}
          {source && (
            <span className="rounded bg-muted px-1.5 py-0.5 text-2xs text-muted-foreground">
              {source}
            </span>
          )}
          {rewritable && (
            <RewriteButton
              fieldLabel={field.label}
              onRewrite={(instruction) => onRewrite(instruction, text)}
            />
          )}
        </div>
      </div>
      <FieldControl
        id={id}
        field={field}
        fact={fact}
        text={text}
        value={value}
        onChange={onChange}
      />
    </div>
  );
}

function FieldControl({
  id,
  field,
  fact,
  text,
  value,
  onChange,
}: {
  id: string;
  field: FieldSpec;
  fact: boolean;
  text: string;
  value: unknown;
  onChange: (value: unknown) => void;
}) {
  switch (field.type) {
    case "textarea":
      return (
        <Textarea
          id={id}
          rows={3}
          value={text}
          readOnly={fact}
          maxLength={field.maxLen ?? undefined}
          onChange={(e) => onChange(e.target.value)}
        />
      );
    case "enum":
      return (
        <Select value={text || undefined} disabled={fact} onValueChange={(v) => onChange(v)}>
          <SelectTrigger id={id} aria-label={field.label} className="h-9 text-sm">
            <SelectValue placeholder="选一个" />
          </SelectTrigger>
          <SelectContent>
            {(field.options ?? []).map((o) => (
              <SelectItem key={o} value={o}>
                {optionLabel(o)}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      );
    case "image":
      // 附录 A.41:没值时说「未设置(可选)」,不说「模板默认图」—— 后者读起来像已经有图了,
      // 用户就不会再管它;而图片本期既没有上传口也不出题,发布也不因它失败(api 侧同条)。
      return (
        <div className="space-y-1">
          <div className="flex flex-wrap items-center gap-2">
            <div
              aria-hidden
              className="grid h-12 w-16 shrink-0 place-items-center rounded-md border border-dashed border-border bg-muted text-muted-foreground"
            >
              <ImageIcon className="h-4 w-4" />
            </div>
            {/* min-w:窄栏(站点基础两列里的 Logo)宁可让按钮换行,也不把这句挤成一个省略号。 */}
            <span className="min-w-[4.5rem] flex-1 truncate text-2xs text-muted-foreground">
              {text || "未设置(可选)"}
            </span>
            <Button type="button" variant="outline" size="sm" disabled title="本期先用模板默认图">
              <Sparkles />
              AI 配图
            </Button>
            <Button type="button" variant="outline" size="sm" disabled title="上传走资产库">
              <Upload />
              上传图片
            </Button>
          </div>
          {!text && (
            <p className="text-2xs text-muted-foreground/70">本期用模板默认图,资产库接入后可上传</p>
          )}
        </div>
      );
    case "text":
    case "phone":
    case "address":
      return (
        <Input
          id={id}
          value={text}
          readOnly={fact}
          maxLength={field.maxLen ?? undefined}
          className="h-9 text-sm"
          onChange={(e) => onChange(e.target.value)}
        />
      );
    default:
      // list / toggle / cta / asset_list / news_feed 本期不在区块表单里编辑,只读显示。
      return <Input id={id} value={displayValue(value)} readOnly className="h-9 text-sm" />;
  }
}
